import $ from 'jquery';
import 'remodal';
import { initLang, setLang } from './lang';

// ------------------------------------------------
//	スクロールアニメーション（.anim → .active）
// ------------------------------------------------
const funcAnim = () => {
	const items = document.querySelectorAll('.anim');
	if (!items.length) return;

	const check = () => {
		const th = window.innerHeight * 0.92;
		// 最下部まで来たら、閾値に届かない要素（フッターなど）も出す
		const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
		items.forEach((el) => {
			if (el.classList.contains('active')) return;
			if (atBottom) {
				el.classList.add('active');
				return;
			}
			const r = el.getBoundingClientRect();
			if (r.top < th && r.bottom > 0) el.classList.add('active');
		});
	};

	check();
	if (document.readyState !== 'complete') {
		window.addEventListener('load', check, { once: true });
	}
	window.addEventListener('scroll', check, { passive: true });
	window.addEventListener('resize', check);
};

// ------------------------------------------------
//	言語メニューの開閉
// ------------------------------------------------
const funcMenu = () => {
	const menu = document.getElementById('menu');
	if (!menu) return;

	const button = menu.querySelector('.menu__current');
	if (!button) return;

	const toggle = (open) => {
		menu.classList.toggle('is-open', open);
		button.setAttribute('aria-expanded', String(open));
	};

	button.addEventListener('click', (e) => {
		e.stopPropagation();
		toggle(!menu.classList.contains('is-open'));
	});

	document.addEventListener('click', (e) => {
		if (!menu.contains(e.target)) toggle(false);
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') toggle(false);
	});

	// 言語切り替え：ページ遷移させず、その場で差し替える（スクロール位置を保つ）
	menu.querySelectorAll('.menu__link').forEach((link) => {
		link.addEventListener('click', (e) => {
			const lang = link.dataset.lang;
			if (!lang) return;
			e.preventDefault();
			setLang(lang);
			toggle(false);
		});
	});
};

// ------------------------------------------------
//	LOOK モーダル
//	モーダルは1つ。開いた LOOK の「サブカット4〜5枚」を横スクロールする（SP はスワイプ）
//	カットは HTML 側の data-cuts（カンマ区切り）が正。写真差し替えは index.html だけ直せばよい
// ------------------------------------------------
const funcLookModal = () => {
	const links = [...document.querySelectorAll('.look__link')];
	const modal = document.querySelector('.hare-modal');
	if (!links.length || !modal) return;

	const track = modal.querySelector('.modal__track');
	const thumbs = modal.querySelector('.modal__thumbs');
	const dots = modal.querySelector('.modal__indicator');
	if (!track) return;

	// カットが2〜3枚しかないので、左右が常に埋まるよう周回ぶん並べる。
	// 中央の周から始めるので、通常の操作で端に達することはまずない
	const LOOPS = 7;
	const CENTER = 3; // 何周目から始めるか（0始まり）
	let cuts = [];
	let current = 0;

	const centerOf = (list, i) => {
		const el = list.children[i];
		if (!el) return 0;
		return el.offsetLeft - (list.clientWidth - el.offsetWidth) / 2;
	};

	// track は周回ぶんあるので、ドットとサムネは「何枚目のカットか」で判定する
	const cutIndex = () => (cuts.length ? current % cuts.length : 0);

	const markCurrent = () => {
		const c = cutIndex();
		[...track.children].forEach((li, i) => li.classList.toggle('is-current', i === current));
		if (dots) [...dots.children].forEach((li, i) => li.classList.toggle('is-current', i === c));
		if (thumbs) {
			[...thumbs.children].forEach((li, i) => li.classList.toggle('is-current', i === c));
			thumbs.scrollTo({ left: centerOf(thumbs, c), behavior: 'smooth' });
		}
	};

	// 端の周まで来た時だけ、見た目そのままで中央の周へ scrollLeft を移す。
	// 中央3周ぶんは触らないので、普通に送っている間はジャンプが起きない
	const normalize = () => {
		const n = cuts.length;
		if (!n) return;
		if (current >= n * (CENTER - 1) && current < n * (CENTER + 2)) return;
		current = n * CENTER + (current % n);
		track.classList.add('is-jumping');
		track.scrollTo({ left: centerOf(track, current), behavior: 'auto' });
		markCurrent();
		requestAnimationFrame(() => {
			requestAnimationFrame(() => track.classList.remove('is-jumping'));
		});
	};

	// 開いた LOOK のカットでモーダルの中身を作り直す
	const build = (link) => {
		const label = link.dataset.look || '';
		cuts = (link.dataset.cuts || '').split(',').map((v) => v.trim()).filter(Boolean);
		if (!cuts.length) {
			const img = link.querySelector('img');
			cuts = img ? [img.getAttribute('src')] : [];
		}
		track.innerHTML = Array.from({ length: cuts.length * LOOPS }, (v, k) => {
			const i = k % cuts.length;
			return `<li><img src="${cuts[i]}" alt="${label} ${i + 1}" width="750" height="1125" loading="lazy" /></li>`;
		}).join('');
		current = cuts.length * CENTER; // 中央の周の1枚目
		if (thumbs) {
			thumbs.innerHTML = cuts
				.map((src, i) => `<li class="over" data-index="${i}"><img src="${src}" alt="${label} ${i + 1}" width="750" height="900" loading="lazy" /></li>`)
				.join('');
		}
		if (dots) {
			dots.innerHTML = cuts.map(() => '<li></li>').join('');
		}
		markCurrent();
	};

	// スクロールが止まったら、中央にいるカットをカレントにする
	let timer = null;
	track.addEventListener('scroll', () => {
		if (timer) clearTimeout(timer);
		timer = setTimeout(() => {
			const center = track.scrollLeft + track.clientWidth / 2;
			let best = current;
			let min = Infinity;
			[...track.children].forEach((li, i) => {
				const d = Math.abs(li.offsetLeft + li.offsetWidth / 2 - center);
				if (d < min) {
					min = d;
					best = i;
				}
			});
			if (best !== current) {
				current = best;
				markCurrent();
			}
			normalize();
		}, 80);
	}, { passive: true });

	const goTo = (i, behavior = 'smooth') => {
		if (!cuts.length) return;
		current = Math.min(Math.max(i, 0), cuts.length * LOOPS - 1);
		markCurrent();
		track.scrollTo({ left: centerOf(track, current), behavior });
	};

	// 何枚目のカットか（0始まり）を指定して移動する。中央の周を使う
	const goToCut = (c, behavior = 'smooth') =>
		goTo(cuts.length * CENTER + ((c + cuts.length) % cuts.length), behavior);

	// トラックが周回ぶんあるので、そのまま隣へ送るだけでループして見える
	const move = (step) => goTo(current + step);

	// ------------------------------------------------
	//	ハッシュで履歴を残す（#look01 …）。戻るボタンで閉じられる／URL 直打ちでその LOOK が開く
	// ------------------------------------------------
	const hashOf = (i) => `#look${String(i + 1).padStart(2, '0')}`;
	const indexOfHash = () => {
		const m = /^#look(\d{2})$/.exec(window.location.hash);
		if (!m) return -1;
		const i = Number(m[1]) - 1;
		return i >= 0 && i < links.length ? i : -1;
	};
	const remodalApi = () => $('[data-remodal-id="look"]').remodal();
	let byHistory = false; // popstate 由来の開閉かどうか

	const open = (i, pushHash) => {
		const link = links[i];
		if (!link) return;
		build(link);
		if (pushHash) window.history.pushState({ look: i }, '', hashOf(i));
		remodalApi().open();
		// モーダルが開ききるまで幅が取れない。1枚目が必ず中央に来るよう数回合わせ直す
		[0, 60, 250, 500].forEach((ms) => setTimeout(() => goToCut(0, 'auto'), ms));
	};

	links.forEach((link, i) => {
		link.addEventListener('click', (e) => {
			e.preventDefault();
			open(i, true);
		});
	});

	// 閉じたらハッシュを外す（戻るで閉じた時は履歴を積まない）
	$(document).on('closed', '.hare-modal', () => {
		if (byHistory) {
			byHistory = false;
			return;
		}
		if (window.location.hash) {
			window.history.pushState(null, '', window.location.pathname + window.location.search);
		}
	});

	window.addEventListener('popstate', () => {
		const i = indexOfHash();
		const opened = document.documentElement.classList.contains('remodal-is-locked');
		if (i >= 0) {
			open(i, false);
		} else if (opened) {
			byHistory = true;
			remodalApi().close();
		}
	});

	// URL に #lookNN が付いた状態で開かれたら、その LOOK を開く
	const initial = indexOfHash();
	if (initial >= 0) setTimeout(() => open(initial, false), 100);

	// 画像の読み込みでレイアウト幅が変わることがあるので、開いている間はリサイズにも追従する
	window.addEventListener('resize', () => {
		if (!document.documentElement.classList.contains('remodal-is-locked')) return;
		goTo(current, 'auto');
	});

	const prevBtn = modal.querySelector('.modal__arrow--prev');
	const nextBtn = modal.querySelector('.modal__arrow--next');
	if (prevBtn) prevBtn.addEventListener('click', () => move(-1));
	if (nextBtn) nextBtn.addEventListener('click', () => move(1));

	if (thumbs) {
		thumbs.addEventListener('click', (e) => {
			const li = e.target.closest('li[data-index]');
			if (!li) return;
			goToCut(Number(li.dataset.index));
		});
	}

	if (dots) {
		dots.addEventListener('click', (e) => {
			const li = e.target.closest('li');
			if (!li) return;
			goToCut([...dots.children].indexOf(li));
		});
	}

	// キーボード（← →）
	document.addEventListener('keydown', (e) => {
		if (!document.documentElement.classList.contains('remodal-is-locked')) return;
		if (e.key === 'ArrowLeft') move(-1);
		if (e.key === 'ArrowRight') move(1);
	});
};

// ------------------------------------------------
//	KV のムービー
//	poster を先に見せ、動画は後から読み込んで差し替える（PC/SP 共通の1本）
// ------------------------------------------------
const funcKvMovie = () => {
	const loading = document.getElementById('loading');
	const root = document.documentElement;
	root.classList.add('is-loading');

	let done = false;
	const hideLoading = () => {
		if (done) return;
		done = true;
		root.classList.remove('is-loading');
		if (loading) loading.classList.add('is-hidden');
	};

	const navi = document.querySelector('.hare__navi');
	// ムービーが出たあと、少し遅れて navi を出す。動画が無い/失敗した時も必ず出す保険つき
	const showNavi = (delay) => setTimeout(() => navi && navi.classList.add('is-shown'), delay);
	setTimeout(() => showNavi(0), 2600);

	// 動画が読めない/遅い時も待たせすぎない
	setTimeout(hideLoading, 6000);

	const video = document.querySelector('.hare__kv-video');
	if (!video) {
		hideLoading();
		showNavi(200);
		return;
	}

	const start = () => {
		const p = video.play();
		if (p && p.catch) p.catch(() => {});
		video.classList.add('is-ready');
		hideLoading();
		showNavi(600); // 動画のフェードイン(0.8s)が進んでから navi を出す
	};

	// canplaythrough（最後まで途切れず再生できる）を待つと長いので、
	// 再生を開始できる canplay の時点で出す。src は HTML に直書きしてあるので読み込みは既に走っている
	if (video.readyState >= 3) {
		start();
	} else {
		video.addEventListener('canplay', start, { once: true });
		// 保険: canplay が来ない環境向け
		video.addEventListener('loadeddata', () => {
			setTimeout(() => {
				if (!video.classList.contains('is-ready')) start();
			}, 800);
		}, { once: true });
	}
};

window.addEventListener('DOMContentLoaded', () => {
	initLang();
	funcKvMovie();
	funcAnim();
	funcMenu();
	funcLookModal();
});
